import {
    ChildTextContentElement,
    OptionItemState,
    TextContentElement,
} from '@deltek/specification-client/core/Models/textContentElement'
import {
    processUnitsOfMeasureElements,
    removeUnitOfMeasureDelimiters,
} from 'components/WorkspaceEditor/components/NodeContents/hooks/useFormatReplacementOption'
import { PROPERTY_EDITOR_EDITABLE_TEXT_VARIANT as NODE_TYPES } from 'pages/ProjectsPage/WorkspacePage/components/constants'
import {
    PROPERTY_EDITOR_PILL_STATE_VARIANT as PillStateVariants,
    PILL_TRACK_CHANGE_STATE as PillTrackChangeState,
} from '../constants'

/**
 * Type guard to check if an element is a ReplacementOption
 */
const isReplacementOption = (
    element: ChildTextContentElement | TextContentElement
): boolean => {
    return element?.type === NODE_TYPES.ReplacementOption
}

/**
 * Type guard to check if an element is a SelectionOption
 */
const isSelectionOption = (element: ChildTextContentElement): boolean => {
    return element.type === NODE_TYPES.SelectionOption
}

/**
 * Processes non-target ReplacementOption elements
 * @param element - The element to process
 * @returns Updated element with appropriate state
 */
const handleNonTargetReplacementOption = (
    element: ChildTextContentElement
): ChildTextContentElement => {
    const changeType = element.changeType?.toUpperCase()

    // Preserve elements that are already modified
    if (changeType === PillTrackChangeState.Modified) {
        return element
    }

    // Set elements to InActive/Disabled if they have no changeType or are reset options
    if (!changeType || changeType === PillTrackChangeState.ResetOption) {
        return {
            ...element,
            optionItemState: PillStateVariants.InActive,
            changeType: PillTrackChangeState.Disabled as 'MODIFIED',
            text: removeUnitOfMeasureDelimiters(element.placeholderText),
        }
    }

    // Return unchanged for other change types
    return element
}

/**
 * Handles updates for ReplacementOption elements with improved separation of concerns
 */
const handleReplacementOptionUpdate = (
    element: ChildTextContentElement,
    isTarget: boolean,
    updatedContentItem: TextContentElement
): ChildTextContentElement => {
    // Delegate non-target processing to dedicated function
    if (!isTarget) {
        return isContentUnmodified(updatedContentItem)
            ? element
            : handleNonTargetReplacementOption(element)
    }

    // Process target element with extracted helper functions
    return handleTargetReplacementOption(element, updatedContentItem)
}

/**
 * Determines if content is unmodified by comparing with placeholder text
 * @param updatedContentItem - The content item to check
 * @returns True if content matches placeholder (unmodified)
 */
const isContentUnmodified = (
    updatedContentItem: TextContentElement
): boolean => {
    return (
        updatedContentItem?.placeholderText != null &&
        updatedContentItem?.text != null &&
        updatedContentItem.placeholderText.trim() ===
            updatedContentItem.text.trim()
    )
}

/**
 * Calculates the new state for a target ReplacementOption element
 * @param currentState - Current option item state
 * @param isUnmodified - Whether content is unmodified
 * @returns New option item state
 */
const calculateTargetReplacementState = (
    currentState: OptionItemState,
    isUnmodified: boolean
): OptionItemState => {
    const isCurrentlyOptedIn = currentState === PillStateVariants.OptedIn
    const isCurrentlyInActive = currentState === PillStateVariants.InActive

    if (isUnmodified && !isCurrentlyInActive) {
        return PillStateVariants.InActive
    }

    if (isCurrentlyOptedIn || isCurrentlyInActive) {
        return null // Toggle InActive to no explicit state
    }

    return PillStateVariants.OptedIn
}

/**
 * Determines the appropriate change type for a target ReplacementOption
 * @param element - The element being updated
 * @param newState - The new option item state
 * @returns Appropriate change type
 */
const getTargetReplacementChangeType = (
    element: ChildTextContentElement,
    newState: OptionItemState
): string | null => {
    const isCurrentlyModified =
        element.changeType?.toUpperCase() === PillTrackChangeState.Modified
    const isCurrentlyInActive =
        element.optionItemState === PillStateVariants.InActive
    const isCurrentlyOptedIn =
        element.optionItemState === PillStateVariants.OptedIn

    if (isCurrentlyModified) {
        return PillTrackChangeState.ResetOption
    }

    if (isCurrentlyInActive) {
        return null
    }

    if (isCurrentlyOptedIn) {
        return PillTrackChangeState.Disabled
    }

    return PillTrackChangeState.Modified
}

/**
 * Processes target ReplacementOption elements
 * @param element - The target element to process
 * @param updatedContentItem - Updated content data
 * @returns Updated element with new state and change type
 */
const handleTargetReplacementOption = (
    element: ChildTextContentElement,
    updatedContentItem: TextContentElement
): ChildTextContentElement => {
    const isUnmodified = isContentUnmodified(updatedContentItem)
    const newState = isUnmodified
        ? null
        : calculateTargetReplacementState(element.optionItemState, isUnmodified)
    const newChangeType = getTargetReplacementChangeType(element, newState)

    // Clean UOM delimiters from text if present
    const cleanedText = updatedContentItem.text
        ? removeUnitOfMeasureDelimiters(updatedContentItem.text)
        : updatedContentItem.text

    return {
        ...element, // Preserve original element properties
        ...updatedContentItem, // Apply updates from updatedContentItem
        text: cleanedText, // Use cleaned text
        optionItemState: newState,
        changeType: newChangeType as 'MODIFIED',
        elements:
            newChangeType === 'MODIFIED'
                ? processUnitsOfMeasureElements(updatedContentItem)
                : element.elements,
    }
}

/**
 * Determines the appropriate state for a SelectionOption element
 */
const getSelectionOptionState = (
    element: ChildTextContentElement,
    isTarget: boolean,
    value: OptionItemState
): OptionItemState => {
    if (isTarget) {
        return value
    }

    const isCurrentlyOptedIn =
        element.optionItemState === PillStateVariants.OptedIn
    return isCurrentlyOptedIn
        ? PillStateVariants.OptedIn
        : PillStateVariants.InActive
}

/**
 * Determines the appropriate change type for a SelectionOption element
 * @param newState - The new state being applied
 * @returns Appropriate change type for the new state
 */
const getSelectionOptionChangeType = (
    newState?: string | null
): string | null => {
    if (!newState) return null

    return newState === PillStateVariants.InActive
        ? PillTrackChangeState.Disabled
        : PillTrackChangeState.Modified
}

/**
 * Handles updates for SelectionOption elements
 */
const handleSelectionOptionUpdate = (
    element: ChildTextContentElement,
    isTarget: boolean,
    value: OptionItemState,
    targetElement?: ChildTextContentElement
): ChildTextContentElement => {
    const isResetTriggeredForReplacementOption =
        targetElement?.type === 'ReplacementOption' &&
        targetElement?.placeholderText?.trim() === targetElement?.text?.trim()
    const newState = isResetTriggeredForReplacementOption
        ? element.optionItemState
        : getSelectionOptionState(element, isTarget, value)
    const newChangeType = getSelectionOptionChangeType(newState)

    return {
        ...element,
        optionItemState: newState,
        changeType: newChangeType as 'MODIFIED',
    }
}

/**
 * Updates elements within an OptionGroup.
 * For OptionGroups: set target element as active with the specified value, others as inactive.
 */
export const handleOptionGroupUpdate = (
    elements: ChildTextContentElement[],
    updatedContentItem: TextContentElement,
    targetId: string,
    value: OptionItemState
): ChildTextContentElement[] => {
    // Input validation
    if (!elements?.length || !targetId || !value) {
        return elements || []
    }

    if (!updatedContentItem) {
        return elements
    }

    try {
        const updatedElements = elements.map((element) => {
            if (!element?.id) {
                return element
            }

            const isTarget = element.id === targetId

            // Handle ReplacementOption
            if (isReplacementOption(element)) {
                return handleReplacementOptionUpdate(
                    element,
                    isTarget,
                    updatedContentItem
                )
            }

            // Handle SelectionOption
            if (isSelectionOption(element)) {
                return handleSelectionOptionUpdate(
                    element,
                    isTarget,
                    value,
                    updatedContentItem?.type === 'ReplacementOption'
                        ? updatedContentItem
                        : undefined
                )
            }

            // No changes for other types
            return element
        })

        return updatedElements
    } catch (error) {
        return elements
    }
}

/**
 * Utility function to validate option item state
 */
export const isValidOptionItemState = (state: string) => {
    return Object.values(PillStateVariants).includes(state as 'OptedIn')
}

/**
 * Utility function to get all elements of a specific type
 */
export const getElementsByType = (
    elements: ChildTextContentElement[],
    type: string
): ChildTextContentElement[] => {
    return elements.filter((element) => element.type === type)
}
